import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import { BANT_CURRENCIES, BantCurrency, BantThresholds } from '../hooks/useBantBusinessRule';

const NUMERIC_INPUT_REGEX = /^\d*(\.\d{0,2})?$/;

interface Props {
  draft: BantThresholds;
  isEnabled: boolean;
  isError: boolean;
  isLoading: boolean;
  isToggling: boolean;
  onDraftChange: (patch: Partial<BantThresholds>) => void;
  onToggle: (nextEnabled: boolean) => void;
}

interface NumericFieldProps {
  id: string;
  label: string;
  value: number;
  onCommit: (next: number) => void;
}

const NumericField = ({ id, label, value, onCommit }: NumericFieldProps): ReactElement => {
  const [input, setInput] = useState(String(value));

  useEffect(() => {
    setInput((current) => (Number(current || 0) === value ? current : String(value)));
  }, [value]);

  const handleChange = (raw: string): void => {
    if (raw !== '' && !NUMERIC_INPUT_REGEX.test(raw)) return;
    setInput(raw);
    onCommit(raw === '' ? 0 : Number(raw));
  };

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-xs text-text-subtle">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        placeholder="0"
        value={input}
        onChange={(e) => handleChange(e.target.value)}
        className="w-44 text-sm bg-bg-surface border border-border-subtle rounded-lg px-3 py-1.5 text-right outline-none focus:border-brand-vivid transition-colors"
      />
    </div>
  );
};

const BantQualificationSection = ({
  draft,
  isEnabled,
  isError,
  isLoading,
  isToggling,
  onDraftChange,
  onToggle,
}: Props): ReactElement => (
  <div className="flex flex-col">
    <div className="flex items-center gap-2.5 mb-2.5">
      <p className="text-xs font-medium text-text-subtle uppercase tracking-wider">Calificación BANT</p>
      <Switch
        aria-label="Activar calificación BANT"
        checked={isEnabled}
        disabled={isLoading || isToggling || isError}
        onCheckedChange={onToggle}
      />
    </div>

    <p className="text-xs text-text-faint mb-3">
      Califica automáticamente a los clientes del subproyecto con la metodología BANT: Budget (capacidad de compra),
      Authority (quién decide), Need (necesidad real) y Time (horizonte de compra).
    </p>

    {isLoading && <p className="text-xs text-text-muted mb-3">Cargando configuración BANT...</p>}

    {isError && (
      <div className="flex items-center gap-2 mb-3 text-xs text-error bg-error-light border border-error-light rounded-lg px-3 py-2">
        No se pudo cargar la configuración BANT del subproyecto.
      </div>
    )}

    {!isLoading && !isError && isEnabled && (
      <div className="bg-bg-surface-raised border border-border-subtle rounded-lg p-3.5 mb-5">
        <div className="border-b border-border-subtle pb-2.5">
          <div className="flex items-center gap-2.5 py-2.5">
            <span className="text-sm text-text-default">Moneda</span>
            <Select value={draft.currency} onValueChange={(next) => onDraftChange({ currency: next as BantCurrency })}>
              <SelectTrigger size="sm" className="w-28 bg-bg-surface" aria-label="Moneda">
                <SelectValue placeholder="Moneda" />
              </SelectTrigger>
              <SelectContent>
                {BANT_CURRENCIES.map((currency) => (
                  <SelectItem key={currency} value={currency}>
                    {currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-text-faint">
            Moneda en la que se expresan los umbrales para medir el budget del cliente; sus montos se convierten a esta
            moneda antes de comparar.
          </p>
        </div>

        <div className="flex flex-col gap-3 pt-3">
          <div className="flex items-center gap-2.5">
            <span className="text-sm text-text-default">Habilitar salario</span>
            <Switch
              aria-label="Habilitar salario"
              checked={draft.salaryEnable}
              onCheckedChange={(next) => onDraftChange({ salaryEnable: next })}
            />
          </div>
          <p className="text-xs text-text-faint">
            Exige que el salario del cliente sea mayor o igual al salario mínimo para calificar su budget.
          </p>
          <NumericField
            id="bant-min-salary"
            label="Salario mínimo"
            value={draft.minSalary}
            onCommit={(next) => onDraftChange({ minSalary: next })}
          />

          <div className="flex items-center gap-2.5 pt-1">
            <span className="text-sm text-text-default">Habilitar ahorro</span>
            <Switch
              aria-label="Habilitar ahorro"
              checked={draft.savingsEnable}
              onCheckedChange={(next) => onDraftChange({ savingsEnable: next })}
            />
          </div>
          <p className="text-xs text-text-faint">
            Exige que el ahorro del cliente sea mayor o igual al ahorro mínimo para calificar su budget.
          </p>
          <NumericField
            id="bant-min-savings"
            label="Ahorro mínimo"
            value={draft.minSavings}
            onCommit={(next) => onDraftChange({ minSavings: next })}
          />
        </div>
      </div>
    )}
  </div>
);

export default BantQualificationSection;
